/**
 * UniverseStage: the brand universe orbit (canvas starfield + 3D orbit camera).
 *
 * Each ring is drawn as its GROUPS: one SVG arc per group, solid when charted and
 * dashed when empty, turning rigidly with its planets on one orbit clock. The
 * clock holds still while the pointer is on the stage, while keyboard focus is in
 * it, and while a fact is open, and it eases in and out so a pause never jumps.
 */
import { BRAND_LOGO_ACCEPT } from "@/hooks/useUploadBrandLogo";
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from "react";
import { DENSITY_PARTS, type Density } from "../data/mock";
import type { StageFact, Ring, RingArc } from "../data/universeLayout";
import { stripFactPrefix } from "../data/universeText";

const E = "cubic-bezier(0.16,1,0.3,1)";
const TAU = Math.PI * 2;
const DEG = Math.PI / 180;
/** Resting camera. Compact screens use a shallower oval so the orbit fills the
 *  width of a phone instead of a square. */
const REST_RX = -60;
const REST_RX_COMPACT = -38;
const REST_RY = -12;
/** An opened fact comes to rest this far above the horizontal centre line. */
const TURN_LIFT = 18 * DEG;
const TURN_MS = 700;
/** Time constant (s) of the orbit clock's rate, so a pause or a resume eases. */
const DRIFT_TAU = 0.28;
const PERSPECTIVE = 1100;
const HINT_COPY = "Drag to turn the orbit. Arrow keys move between facts; Enter opens one.";

type Star = { x: number; y: number; r: number; al: number; tw: number; ph: number; lime: boolean };
type Streak = { ang: number; born: number; c: string };
type Cam = { rx: number; ry: number; sc: number };

/** Angle folded into (-PI, PI]. */
function wrapPi(a: number): number {
  let x = (a + Math.PI) % TAU;
  if (x < 0) x += TAU;
  return x - Math.PI;
}

/** A CSS cubic-bezier as a JS easing (Newton steps on x, then y). */
function cubicBezier(x1: number, y1: number, x2: number, y2: number) {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number) => ((ay * t + by) * t + cy) * t;
  const slopeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const err = sampleX(t) - x;
      if (Math.abs(err) < 1e-5) break;
      const d = slopeX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= err / d;
    }
    return sampleY(Math.max(0, Math.min(1, t)));
  };
}
const HOUSE_EASE = cubicBezier(0.16, 1, 0.3, 1);

/**
 * Project an orbit point to stage space (820x520, scene origin at 410,258). This
 * is the one projection the planets, the lines, the tooltip and the keyboard all
 * share, so they agree at any tilt and zoom.
 */
function project(ang: number, R: number, yoff: number, cam: Cam): [number, number, number] {
  const rxr = cam.rx * DEG;
  const ryr = cam.ry * DEG;
  const cosrx = Math.cos(rxr), sinrx = Math.sin(rxr), cosry = Math.cos(ryr), sinry = Math.sin(ryr);
  const px = Math.cos(ang) * R, pz = Math.sin(ang) * R, py = yoff;
  const rx1 = px * cosry + pz * sinry;
  const rz1 = -px * sinry + pz * cosry;
  const ry1 = py * cosrx - rz1 * sinrx;
  const rzc = py * sinrx + rz1 * cosrx;
  const k = PERSPECTIVE / (PERSPECTIVE - cam.sc * rzc);
  return [410 + cam.sc * rx1 * k, 258 + cam.sc * ry1 * k, rzc];
}

/** SVG path for one arc of radius r between two ring-local angles. */
function arcPath(r: number, start: number, end: number): string {
  const sweep = end - start;
  const x0 = (Math.cos(start) * r).toFixed(2), y0 = (Math.sin(start) * r).toFixed(2);
  const x1 = (Math.cos(end) * r).toFixed(2), y1 = (Math.sin(end) * r).toFixed(2);
  return `M ${x0} ${y0} A ${r} ${r} 0 ${sweep > Math.PI ? 1 : 0} 1 ${x1} ${y1}`;
}

type Props = {
  facts: StageFact[];
  rings: Ring[];
  /** similarity relations per fact id (weight-sorted, capped at 5) */
  related?: Record<string, string[]>;
  /** fact ids learned since the last visit: the arrival glint */
  freshIds?: string[];
  /** A fact lit from outside the stage (a hovered row in the list). It gets the
      full hover state: tooltip, lines and its group arc. */
  highlightFactId?: string | null;
  /** Fired when the stage's OWN hovered or keyboard-active planet changes. */
  onHoverFact?: (factId: string | null) => void;
  /** A ring lit from outside the stage (a hovered subject row, a chosen chip). */
  litRingId?: string | null;
  /** Fired when the pointer lights a ring band on the stage itself. */
  onHoverRing?: (ringId: string | null) => void;
  /** Controlled selection: the fact whose card is open. */
  selectedFactId?: string | null;
  /** Fired when the stage selects or clears a fact. */
  onSelectFact?: (id: string | null) => void;
  brandInitial: string;
  /** the brand's presigned logo, drawn in the sun core; null = the initial */
  logoUrl?: string | null;
  /** uploads a picked logo file. Absent = the core is a plain mark. */
  onUploadLogo?: (file: File) => void;
  /** true while a logo upload is in flight */
  uploadingLogo?: boolean;
  density: Density;
  motion: boolean;
  dark: boolean;
  /** play the one-time Assembly reveal (owned by the view) */
  intro?: boolean;
  /** show the drag/zoom manipulation chrome (first-visit hint + zoom cluster) */
  showControls?: boolean;
  /** render the screen-reader list of facts. Defaults to showControls. */
  showFactList?: boolean;
  /** Fit every ring and off-ring fact inside the stage at its resting zoom. */
  containAllFacts?: boolean;
  /** Let compact touch screens use the stage as part of the page's scroll. */
  allowVerticalPan?: boolean;
  /** Compact layout: shallow resting camera, fitted to the oval it draws. */
  compact?: boolean;
  /** Review mode: the group a proposed fact would be filed in. Marks the middle
      of that arc (Universe / Stage · Lands here) and holds the orbit still. */
  landsHere?: { arcKey: string; label: string } | null;
};

function UniverseStage({ facts, rings, related, freshIds, highlightFactId, onHoverFact, litRingId = null, onHoverRing, selectedFactId, onSelectFact, brandInitial, logoUrl = null, onUploadLogo, uploadingLogo = false, density, motion, dark, intro = false, showControls = true, showFactList, containAllFacts = false, allowVerticalPan = false, compact = false, landsHere = null }: Props) {
  const outerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<HTMLDivElement>(null);
  // The rings live in their OWN 3D root, a sibling painted before the planets'.
  // Sharing one preserve-3d context put every billboarded marker through a ring
  // plane, which sliced dots, squares and outlines into wedges. Both roots get
  // the identical camera transform every frame.
  const ringRootRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const landsMarkRef = useRef<HTMLDivElement>(null);
  const landsLabelRef = useRef<HTMLDivElement>(null);
  const landsArcRef = useRef<string | null>(null);
  const uploadTipRef = useRef<HTMLDivElement>(null);
  landsArcRef.current = landsHere?.arcKey ?? null;
  // Where the stage sits inside the wrapper, and the tooltip's size, cached so
  // the loop does not read layout every frame while a planet is lit. Refreshed
  // while `layoutFreshUntilRef` is in the future (a resize or a scale change is
  // settling) and whenever the tooltip's content changes.
  const layoutCacheRef = useRef<{ offX: number; offY: number; k: number; w: number; h: number } | null>(null);
  const layoutFreshUntilRef = useRef(0);
  const tipSizeRef = useRef<{ w: number; h: number } | null>(null);
  const restRx = compact ? REST_RX_COMPACT : REST_RX;
  const camRx = useRef(restRx);
  const camRy = useRef(REST_RY);
  // Displayed camera per frame (settle interpolation + intro scale included),
  // mirrored so the pointer-to-plane ring hit-test uses EXACTLY the projection on
  // screen.
  const camDispRef = useRef<Cam>({ rx: restRx, ry: REST_RY, sc: 1 });
  const restRxRef = useRef(restRx);
  const motionRef = useRef(motion);
  // Live data mirrored into refs so the rAF loop reads the latest each frame
  // WITHOUT the effect tearing down + restarting on every graph refetch.
  const factsRef = useRef<StageFact[]>(facts);
  const ringsRef = useRef<Ring[]>(rings);
  const entranceStartRef = useRef<number[]>([]);
  const factElsRef = useRef<HTMLElement[]>([]);
  const echoElsRef = useRef<HTMLElement[]>([]);
  const ringSvgElsRef = useRef<Array<SVGSVGElement | null>>([]);
  // The orbit clock (seconds of drift) and its eased rate. Planets, echoes and
  // ring phases all read this clock, never the wall clock.
  const orbitClockRef = useRef(0);
  const orbitRateRef = useRef(1);
  // Per-lane phase added on top of the clock: the opened fact's ring turns by
  // easing its own entry here. The last slot is the off-lane.
  const ringOffsetRef = useRef<number[]>([]);
  const turnRef = useRef<{ lane: number; from: number; to: number; t0: number } | null>(null);
  const pointerInsideRef = useRef(false);
  const kbdFocusRef = useRef(false);

  const [scale, setScale] = useState(1);
  // User zoom. The contained fit already holds the whole orbit, so it rests at 1.
  const [zoom, setZoom] = useState(containAllFacts ? 1 : 0.86);
  const fitBox = useMemo(() => {
    if (!containAllFacts) return { width: 820, height: 520, offsetY: 0 };
    // Fit the oval the orbit actually draws at the resting camera (the off-lane
    // included when off-ring facts exist), not the sphere a full rotation could
    // sweep: the stage clips at its edge during a drag instead. Perspective makes
    // the near half taller than the far half, so the oval, not the sun, is
    // centred in the stage.
    const orbitRadius = Math.max(
      rings.reduce((max, ring) => Math.max(max, ring.radius), 0),
      facts.reduce((max, fact) => Math.max(max, Math.hypot(fact.R, fact.yoff)), 0),
    );
    const paddedRadius = orbitRadius + 10;
    const cam: Cam = { rx: compact ? REST_RX_COMPACT : REST_RX, ry: 0, sc: 1 };
    let maxX = 0, minY = 0, maxY = 0;
    for (let k = 0; k < 96; k++) {
      const [x, y] = project((k / 96) * TAU, paddedRadius, 0, cam);
      maxX = Math.max(maxX, Math.abs(x - 410));
      minY = Math.min(minY, y - 258);
      maxY = Math.max(maxY, y - 258);
    }
    return { width: maxX * 2 + 16, height: maxY - minY + 16, offsetY: -(maxY + minY) / 2 };
  }, [containAllFacts, compact, facts, rings]);
  const fileRef = useRef<HTMLInputElement>(null);
  // The logo is a presigned URL that expires. A failure is remembered per URL,
  // so the refetch that brings a fresh one draws the logo again.
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);
  const showLogo = !!logoUrl && failedLogoUrl !== logoUrl;
  const onLogoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Cleared so picking the same file again still fires a change.
    e.target.value = "";
    if (file) onUploadLogo?.(file);
  };
  const [selected, setSelected] = useState<number | null>(null);
  // The pointer's planet, held by id like the keyboard's, so a refetch that
  // reorders the facts cannot move it onto a different planet.
  const [hoverId, setHoverId] = useState<string | null>(null);
  // The keyboard's roving active fact, held by id so a refetch that reorders the
  // facts cannot move it onto a different planet.
  const [kbdId, setKbdId] = useState<string | null>(null);
  const [liveMsg, setLiveMsg] = useState("");
  // which orbit ring the pointer is over (empty-band hover); planet hover yields.
  const [hoverRing, setHoverRing] = useState<number | null>(null);
  const [moved, setMoved] = useState(false);
  const movedRef = useRef(false);

  const indexById = useMemo(() => new Map(facts.map((f, i) => [f.id, i] as const)), [facts]);
  const hoverIdx = hoverId != null ? (indexById.get(hoverId) ?? null) : null;
  const kbdIdx = kbdId != null ? (indexById.get(kbdId) ?? null) : null;
  const highlightIdx = highlightFactId != null ? (indexById.get(highlightFactId) ?? null) : null;
  // One lit planet: the pointer wins, then the keyboard, then the list.
  const focusIdx = hoverIdx ?? kbdIdx ?? highlightIdx;
  const focusFact = focusIdx != null ? facts[focusIdx] : null;
  const selFact = selected != null ? facts[selected] : null;

  // Lines run from the lit planet to its similarity neighbours. None from the
  // opened fact itself: its card lists them as "Close to".
  const relIdxs = useMemo(() => {
    if (focusIdx == null || focusIdx === selected || !related) return [] as number[];
    const ids = related[facts[focusIdx]?.id ?? ""] ?? [];
    return ids.slice(0, 5).map((id) => indexById.get(id) ?? -1).filter((i) => i >= 0);
  }, [focusIdx, selected, related, facts, indexById]);
  const relSet = useMemo(() => new Set(relIdxs), [relIdxs]);
  const relIdxsRef = useRef<number[]>([]);
  const relGroupRef = useRef<SVGGElement>(null);

  // Selection: the opened fact's group-mates stay bright; everything else dims.
  const mateSet = useMemo(() => {
    const out = new Set<number>();
    if (!selFact?.groupKey) return out;
    facts.forEach((f, i) => { if (f.groupKey === selFact.groupKey) out.add(i); });
    return out;
  }, [facts, selFact]);
  const keepSet = useMemo(() => {
    if (selected == null) return null;
    const keep = new Set<number>(mateSet);
    keep.add(selected);
    if (focusIdx != null) keep.add(focusIdx);
    relIdxs.forEach((i) => keep.add(i));
    return keep;
  }, [selected, mateSet, focusIdx, relIdxs]);
  const keepRef = useRef<Set<number> | null>(null);
  const selGroupRef = useRef<string>("");

  // Echoes flattened across rings, each tied to its fact for the entrance ramp.
  const echoes = useMemo(
    () =>
      rings.flatMap((ring, lane) =>
        ring.echoes.map((echo) => ({ ...echo, lane, fi: indexById.get(echo.factId) ?? -1 })),
      ),
    [rings, indexById],
  );
  const echoesRef = useRef(echoes);

  const litRingIdx = hoverRing ?? (litRingId ? rings.findIndex((r) => r.id === litRingId) : -1);
  const litRing = litRingIdx != null && litRingIdx >= 0 ? litRingIdx : null;
  const litArcKeys = useMemo(() => {
    const keys = new Set<string>();
    if (focusFact?.groupKey) keys.add(focusFact.groupKey);
    if (selFact?.groupKey) keys.add(selFact.groupKey);
    return keys;
  }, [focusFact, selFact]);

  // Arrival glint: ids learned since the last visit ping twice on entry, PLUS
  // ids that arrive live during this session (e.g. accepting a proposed fact).
  const [liveArrivedIds, setLiveArrivedIds] = useState<string[]>([]);
  const seenFactIdsRef = useRef<Set<string> | null>(null);
  const freshSet = useMemo(
    () => new Set([...(freshIds ?? []), ...liveArrivedIds]),
    [freshIds, liveArrivedIds],
  );
  const glintDelayFor = useCallback(
    (id: string): number => {
      const liveIdx = liveArrivedIds.indexOf(id);
      if (liveIdx >= 0) return liveIdx * 0.12;
      const freshIdx = freshIds?.indexOf(id) ?? -1;
      return (intro ? 1.9 : 0.6) + Math.max(0, freshIdx) * 0.12;
    },
    [liveArrivedIds, freshIds, intro],
  );

  useEffect(() => {
    motionRef.current = motion;
  }, [motion]);

  // mirror hover/selection into refs the animation loop can read each frame
  const focusRef = useRef<number | null>(null);
  const selectedRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    focusRef.current = focusIdx;
    selectedRef.current = selected;
    relIdxsRef.current = relIdxs;
    keepRef.current = keepSet;
    selGroupRef.current = selFact?.groupKey ?? "";
    echoesRef.current = echoes;
  }, [focusIdx, selected, relIdxs, keepSet, selFact, echoes]);

  // Controlled-selection sync: mirror the parent's id into the index-based
  // internal `selected` everything downstream reads. Unknown ids resolve to null.
  useEffect(() => {
    if (selectedFactId === undefined) return;
    const idx = selectedFactId ? (indexById.get(selectedFactId) ?? -1) : -1;
    setSelected(idx >= 0 ? idx : null);
  }, [selectedFactId, indexById]);

  /** Select or clear a fact BY ID (pointer, keyboard, list): an index captured
   *  at pointerdown can point at a different fact after a refetch. */
  const selectFactId = useCallback(
    (id: string | null) => {
      if (onSelectFact) onSelectFact(id);
      else setSelected(id != null ? (indexById.get(id) ?? null) : null);
    },
    [onSelectFact, indexById],
  );
  const selectFact = useCallback(
    (i: number | null) => selectFactId(i == null ? null : facts[i]?.id ?? null),
    [selectFactId, facts],
  );
  // Surface the stage's own lit planet (pointer or keyboard) to the view, by id.
  const ownId = (hoverIdx != null ? hoverId : null) ?? (kbdIdx != null ? kbdId : null);
  useEffect(() => {
    onHoverFact?.(ownId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownId]);
  useEffect(() => {
    onHoverRing?.(hoverRing != null ? rings[hoverRing]?.id ?? null : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoverRing]);
  // The tooltip's content changed: measure it again on the next lit frame.
  const tipKey = focusIdx != null ? facts[focusIdx]?.id ?? "" : "";
  useLayoutEffect(() => {
    tipSizeRef.current = null;
  }, [tipKey]);
  // hovered ring mirrored to a ref so the rAF loop can park the 2D lane tag at
  // that ring's projected apex each frame.
  const hoverRingRef = useRef<number | null>(null);
  useEffect(() => { hoverRingRef.current = hoverRing; }, [hoverRing]);
  const laneTagPlateRef = useRef<HTMLDivElement>(null);
  const laneTagLeaderRef = useRef<HTMLDivElement>(null);
  const laneRadiusRef = useRef(0);
  const prevHrRef = useRef<number | null>(null);

  // First interaction retires the controls hint (persisted so it never returns).
  const markInteracted = useCallback(() => {
    try { localStorage.setItem("co:universe:hintSeen", "1"); } catch { /* storage blocked */ }
  }, []);

  // Zoom rides pinch (Chrome delivers trackpad pinch as ctrl+wheel) or
  // cmd/ctrl+wheel; a PLAIN scroll passes through to the page.
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      if (!movedRef.current) { movedRef.current = true; setMoved(true); markInteracted(); }
      setZoom((z) => Math.max(0.55, Math.min(2.2, +(z - e.deltaY * 0.0016).toFixed(3))));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [markInteracted]);

  const [prefersReduced, setPrefersReduced] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPrefersReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const reduced = prefersReduced || !motion;
  // Sun core. Without a logo it is the accent disc with the brand initial. With
  // one there is no disc: the logo is drawn uncropped in its own shape.
  const sunCoreStyle: CSSProperties = { position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: 48, height: 48, borderRadius: showLogo ? 0 : "50%", background: showLogo ? "transparent" : "var(--co-accent)", border: "none", padding: 0, display: "grid", placeItems: "center", boxShadow: showLogo ? "none" : "0 0 16px rgba(var(--co-accent-rgb),.32)", overflow: showLogo ? "visible" : "hidden", opacity: uploadingLogo ? 0.6 : 1, transition: `opacity .2s ${E}`, animation: intro && !reduced ? `coSunRise .42s ${E} .2s both` : undefined };
  const sunCoreMark = showLogo ? (
    <img src={logoUrl ?? undefined} alt="" onError={() => setFailedLogoUrl(logoUrl)} style={{ width: "100%", height: "100%", objectFit: "contain", filter: "drop-shadow(0 0 8px rgba(var(--co-accent-rgb),.34))" }} />
  ) : (
    <span style={{ fontFamily: "var(--font-section)", fontWeight: 800, fontSize: 20, color: "var(--co-on-accent)", lineHeight: 1 }}>{brandInitial}</span>
  );
  const sunCoreLabel = showLogo ? "Change your brand logo" : "Upload your brand logo";
  const prefersReducedRef = useRef(prefersReduced);
  useEffect(() => { prefersReducedRef.current = prefersReduced; }, [prefersReduced]);

  // ---- the Assembly (one-time reveal) state machine ----
  const introRef = useRef(intro);
  useEffect(() => { introRef.current = intro; }, [intro]);
  const [introDone, setIntroDone] = useState(() => !intro);
  const introDoneRef = useRef(!intro);
  const t0Ref = useRef(0);            // rAF clock origin
  const camOwnedRef = useRef(false);  // once grabbed, the auto-settle yields to the user
  const [showHint] = useState(() => {
    try { return localStorage.getItem("co:universe:hintSeen") !== "1"; } catch { return true; }
  });
  const [hintAutoHidden, setHintAutoHidden] = useState(false);

  // The Assembly plays through (2400ms) and drops its one-shots. It has no
  // skip: the pill and Esc were removed (user call, 12 Oct).
  useEffect(() => {
    if (!intro || introDoneRef.current) return;
    const timer = setTimeout(() => {
      introDoneRef.current = true;
      setIntroDone(true);
    }, 2400);
    return () => clearTimeout(timer);
  }, [intro]);

  // auto-fade the control hint 8s after it appears, even without interaction
  useEffect(() => {
    if (!showHint) return;
    const timer = setTimeout(() => setHintAutoHidden(true), (intro ? 2300 : 300) + 8000);
    return () => clearTimeout(timer);
  }, [showHint, intro]);

  // A change of layout (desk to phone) moves the camera to that layout's rest.
  useEffect(() => {
    if (restRxRef.current === restRx) return;
    const from = camRx.current;
    const shift = restRx - restRxRef.current;
    restRxRef.current = restRx;
    const to = Math.max(-86, Math.min(-8, from + shift));
    if (prefersReducedRef.current) { camRx.current = to; return; }
    const t0 = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const p = HOUSE_EASE(Math.min(1, (now - t0) / 520));
      camRx.current = from + (to - from) * p;
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [restRx]);

  /** Re-read the planet + echo elements from the scene into the loop's cache. */
  const syncFactEls = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    factElsRef.current = Array.from(scene.querySelectorAll<HTMLElement>("[data-fact]"));
    echoElsRef.current = Array.from(scene.querySelectorAll<HTMLElement>("[data-echo]"));
  }, []);

  /** A lane's current phase: the orbit clock times its speed, plus its turn. */
  const lanePhase = useCallback((lane: number, speed: number) => {
    return orbitClockRef.current * speed + (ringOffsetRef.current[lane] ?? 0);
  }, []);

  /**
   * Put every planet, echo and ring arc on its orbit for wall time `t`, using the
   * camera the last rendered frame used. Extracted from the rAF loop so freshly
   * mounted planets are placed SYNCHRONOUSLY the moment they commit, instead of
   * being painted at the scene origin until the loop next looks at them.
   */
  const placeFacts = useCallback((t: number) => {
    if (factElsRef.current.length !== factsRef.current.length || echoElsRef.current.length !== echoesRef.current.length) syncFactEls();
    const isReduced = prefersReducedRef.current || !motionRef.current;
    const { rx, ry } = camDispRef.current;
    const rxr = rx * DEG;
    const ryr = ry * DEG;
    const cosrx = Math.cos(rxr), sinrx = Math.sin(rxr), cosry = Math.cos(ryr), sinry = Math.sin(ryr);
    const bb = ` rotateY(${(-ry).toFixed(2)}deg) rotateX(${(-rx).toFixed(2)}deg)`;
    const offLane = ringsRef.current.length;
    const keep = keepRef.current;
    const entranceOf = (i: number) => {
      if (isReduced) return 1;
      const e = Math.max(0, Math.min(1, (t - (entranceStartRef.current[i] ?? 0)) / 0.55));
      return 1 - Math.pow(1 - e, 3);
    };
    const put = (el: HTMLElement, ang: number, R: number, y: number, ent: number, dim: number, lift: boolean) => {
      const x = Math.cos(ang) * R;
      const z = Math.sin(ang) * R;
      const z1 = -x * sinry + z * cosry;
      const zc = y * sinrx + z1 * cosrx;
      const norm = Math.max(-1, Math.min(1, zc / 280));
      const ds = (0.78 + 0.22 * ((norm + 1) / 2)) * (0.45 + 0.55 * ent);
      el.style.transform = `translate3d(${x.toFixed(1)}px,${y}px,${z.toFixed(1)}px)${bb} scale(${ds.toFixed(3)})`;
      // Depth fades the far side, but never below legibility at rest.
      el.style.opacity = ((0.55 + 0.45 * ((norm + 1) / 2)) * ent * dim).toFixed(3);
      el.style.zIndex = lift ? "4000" : String(1000 + Math.round(zc));
    };

    factElsRef.current.forEach((el) => {
      const i = +(el.getAttribute("data-fact") || "0");
      const f = factsRef.current[i];
      if (!f) return;
      const lane = f.ring >= 0 ? f.ring : offLane;
      const ang = f.ang0 + lanePhase(lane, f.speed);
      const lift = i === focusRef.current || i === selectedRef.current;
      const dim = keep && !keep.has(i) ? 0.4 : 1;
      put(el, ang, f.R, f.yoff, entranceOf(i), dim, lift);
    });
    echoElsRef.current.forEach((el) => {
      const k = +(el.getAttribute("data-echo") || "0");
      const echo = echoesRef.current[k];
      const ring = echo ? ringsRef.current[echo.lane] : undefined;
      if (!echo || !ring) return;
      const ang = echo.ang0 + lanePhase(echo.lane, ring.speed);
      const dim = keep && echo.arcKey !== selGroupRef.current ? 0.4 : 1;
      put(el, ang, ring.radius, 0, echo.fi >= 0 ? entranceOf(echo.fi) : 1, dim, false);
    });
    // The rings turn rigidly with their planets. After the ring's rotateX(90deg),
    // local SVG (x, y) is scene (x, z), so an SVG angle IS a planet angle and a
    // rotate() by the lane phase carries the arcs exactly as far as the planets.
    ringSvgElsRef.current.forEach((svg, lane) => {
      const ring = ringsRef.current[lane];
      if (!svg || !ring) return;
      svg.style.transform = `rotate(${(lanePhase(lane, ring.speed) / DEG).toFixed(3)}deg)`;
    });
  }, [syncFactEls, lanePhase]);

  // Sync live data + derived entrance timings into refs whenever facts/rings
  // change. A LAYOUT effect so the refs are current before the browser paints
  // the planets this render just added.
  useLayoutEffect(() => {
    factsRef.current = facts;
    ringsRef.current = rings;
    const playing = introRef.current;
    const estTotal = facts.filter((f) => f.kind !== "new").length;
    const newTotal = facts.length - estTotal;
    const entranceStart: number[] = new Array(facts.length).fill(0);
    let eIdx = 0, nIdx = 0;

    // Live-arrival detection: an id this stage has never rendered just showed up
    // mid-session, so ITS entrance starts now instead of at the stale mount time.
    const seen = seenFactIdsRef.current;
    const isFirstRun = seen === null;
    const nextSeen = new Set(seen ?? []);
    const arrivedNow: string[] = [];
    const nowT = (performance.now() - t0Ref.current) / 1000;

    facts.forEach((f, i) => {
      const isLiveArrival = !isFirstRun && !nextSeen.has(f.id);
      if (f.kind === "new") {
        entranceStart[i] = playing ? 1.55 + (nIdx / Math.max(1, newTotal - 1)) * 0.36 : 1.15 + nIdx * 0.14;
        nIdx++;
      } else {
        entranceStart[i] = playing ? 0.98 + (eIdx / Math.max(1, estTotal - 1)) * 0.7 : 0.15 + eIdx * 0.09;
        eIdx++;
      }
      if (isLiveArrival) {
        entranceStart[i] = Math.max(0, nowT);
        arrivedNow.push(f.id);
      }
      nextSeen.add(f.id);
    });
    entranceStartRef.current = entranceStart;
    seenFactIdsRef.current = nextSeen;
    if (arrivedNow.length) {
      setLiveArrivedIds((prev) => [...prev, ...arrivedNow].slice(-24));
    }
  }, [facts, rings]);

  // Re-query the fact DOM nodes when facts change, then place them immediately,
  // before paint, so a batch of arrivals never piles up at the scene origin.
  useLayoutEffect(() => {
    syncFactEls();
    if (t0Ref.current !== 0) placeFacts((performance.now() - t0Ref.current) / 1000);
  }, [facts, echoes, syncFactEls, placeFacts]);

  // THE TURN. Opening (or stepping to) a fact eases ONLY its own ring so the fact
  // comes to rest on the right of the orbit, toward the card, a little above the
  // centre line, at the current camera. Reduced motion jumps straight there.
  const selectedId = selFact?.id ?? null;
  useEffect(() => {
    if (!selectedId) return;
    const f = factsRef.current.find((x) => x.id === selectedId);
    if (!f) return;
    const offsets = ringOffsetRef.current;
    const running = turnRef.current;
    if (running) {
      offsets[running.lane] = running.to;
      turnRef.current = null;
    }
    const lane = f.ring >= 0 ? f.ring : ringsRef.current.length;
    const current = offsets[lane] ?? 0;
    const angle = f.ang0 + orbitClockRef.current * f.speed + current;
    const rxr = camRx.current * DEG;
    const target = camRy.current * DEG + TURN_LIFT * (Math.sin(rxr) < 0 ? -1 : 1);
    const to = current + wrapPi(target - angle);
    if (prefersReducedRef.current || !motionRef.current) {
      offsets[lane] = to;
      if (t0Ref.current !== 0) placeFacts((performance.now() - t0Ref.current) / 1000);
      return;
    }
    turnRef.current = { lane, from: current, to, t0: performance.now() };
  }, [selectedId, placeFacts]);

  // ---- animation loop ----
  useEffect(() => {
    const canvas = canvasRef.current;
    const scene = sceneRef.current;
    const outer = outerRef.current;
    if (!canvas || !scene || !outer) return;

    const dpr = Math.min(3, (window.devicePixelRatio || 1) * 1.5);
    canvas.width = 820 * dpr;
    canvas.height = 520 * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const dens = DENSITY_PARTS[density];
    // theme palette for the canvas (canvas cannot read CSS vars)
    const accentRgb = dark ? "207, 255, 10" : "32, 124, 245";
    const starDim = dark ? "#8a8a8a" : "#c9c9c9";
    const starAccent = dark ? "#cfff0a" : "#207cf5";
    // A faint bloom: the sun names the centre, it must not outweigh the page's
    // one filled button.
    const glowPeak = dark ? 0.2 : 0.13;
    const streakColor = `rgba(${accentRgb}, .85)`;
    const stars: Star[] = [];
    for (let i = 0; i < dens.parts + 14; i++) {
      stars.push({ x: Math.random() * 820, y: Math.random() * 520, r: 0.5 + Math.random() * 0.9, al: 0.09 + Math.random() * 0.28, tw: 0.5 + Math.random() * 1.1, ph: Math.random() * 6.28, lime: false });
    }
    let streaks: Streak[] = [];
    let nextSpawn = introRef.current && !introDoneRef.current ? 2.6 : 0.6;
    const spawnGap = dens.gap;
    t0Ref.current = introRef.current && !introDoneRef.current ? performance.now() : performance.now() - 4000;
    let raf = 0;
    let lastNow = performance.now();
    const isReduced = () => prefersReduced || !motionRef.current;

    const measure = () => {
      const w = outer.clientWidth || 820;
      const h = outer.clientHeight || 520;
      const fitted = Math.min(w / fitBox.width, h / fitBox.height, 1.45);
      const s = containAllFacts ? fitted : Math.max(0.55, fitted);
      setScale((prev) => (Math.abs(s - prev) > 0.001 ? s : prev));
    };

    const scene3d = (now: number) => {
      const t = (now - t0Ref.current) / 1000;
      const dt = Math.min(0.1, Math.max(0, (now - lastNow) / 1000));
      lastNow = now;
      // The orbit clock: advances only while nothing holds it, easing its rate so
      // a pause and a resume are both smooth. Reduced motion never drifts.
      const held = pointerInsideRef.current || kbdFocusRef.current || selectedRef.current != null || landsArcRef.current != null;
      const targetRate = isReduced() || held ? 0 : 1;
      orbitRateRef.current += (targetRate - orbitRateRef.current) * (1 - Math.exp(-dt / DRIFT_TAU));
      // The ease is asymptotic; snap its tail so a held orbit is truly still.
      if (isReduced() || (targetRate === 0 && orbitRateRef.current < 0.004)) orbitRateRef.current = 0;
      orbitClockRef.current += dt * orbitRateRef.current;
      const turn = turnRef.current;
      if (turn) {
        const p = Math.min(1, (now - turn.t0) / TURN_MS);
        ringOffsetRef.current[turn.lane] = turn.from + (turn.to - turn.from) * HOUSE_EASE(p);
        if (p >= 1) turnRef.current = null;
      }

      // camera settle (intro only): scene scale 0.86->1 with a gentle tilt/pan
      // resolve. Any grab claims the camera and the settle stops without snapping.
      let pc = Math.min(1, t / 1.6);
      pc = 1 - Math.pow(1 - pc, 3);
      if (isReduced()) pc = 1;
      const playing = introRef.current && !isReduced();
      const settling = playing && !camOwnedRef.current;
      const sc = playing ? 0.86 + 0.14 * pc : 1;
      const startRx = restRxRef.current - 8;
      const startRy = REST_RY - 6;
      const rx = settling ? startRx + (camRx.current - startRx) * pc : camRx.current;
      const ry = settling ? startRy + (camRy.current - startRy) * pc : camRy.current;
      const cam = camDispRef.current;
      cam.rx = rx;
      cam.ry = ry;
      cam.sc = sc;
      const camTransform = `scale(${sc.toFixed(3)}) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;
      scene.style.transform = camTransform;
      if (ringRootRef.current) ringRootRef.current.style.transform = camTransform;
      placeFacts(t);

      const offLane = ringsRef.current.length;
      const projFact = (fi: number): [number, number, number] | null => {
        const f = factsRef.current[fi];
        if (!f) return null;
        const lane = f.ring >= 0 ? f.ring : offLane;
        return project(f.ang0 + lanePhase(lane, f.speed), f.R, f.yoff, cam);
      };

      // Lines from the lit planet to its neighbours, in the 2D overlay. Their
      // endpoints share the planets' exact projection so they pin to the dots.
      const relG = relGroupRef.current;
      const fIdx = focusRef.current;
      const rels = relIdxsRef.current;
      const a = fIdx != null ? projFact(fIdx) : null;
      if (relG && a && rels.length) {
        relG.querySelectorAll("line").forEach((ln) => {
          const b = projFact(+(ln.getAttribute("data-rel") || "-1"));
          if (!b) { ln.style.opacity = "0"; return; }
          ln.setAttribute("x1", a[0].toFixed(1));
          ln.setAttribute("y1", a[1].toFixed(1));
          ln.setAttribute("x2", b[0].toFixed(1));
          ln.setAttribute("y2", b[1].toFixed(1));
          ln.style.opacity = "1";
        });
      }

      // The tooltip: a 2D card beside the lit planet, on the side away from its
      // lines where it can, kept inside the stage.
      const tip = tooltipRef.current;
      const stageEl = stageRef.current;
      if (tip && stageEl && a) {
        let lay = layoutCacheRef.current;
        if (!lay || now < layoutFreshUntilRef.current) {
          const sr = stageEl.getBoundingClientRect();
          const wrr = outer.getBoundingClientRect();
          lay = { offX: sr.left - wrr.left, offY: sr.top - wrr.top, k: sr.width / 820, w: wrr.width, h: wrr.height };
          layoutCacheRef.current = lay;
        }
        let tipSize = tipSizeRef.current;
        if (!tipSize) {
          tipSize = { w: tip.offsetWidth, h: tip.offsetHeight };
          tipSizeRef.current = tipSize;
        }
        const { offX, offY, k } = lay;
        const wr = { width: lay.w, height: lay.h };
        const toWrap = (p: [number, number, number]): [number, number] => [offX + p[0] * k, offY + p[1] * k];
        const [px, py] = toWrap(a);
        let mdx = 0, mdy = 0;
        rels.forEach((ri) => {
          const b = projFact(ri);
          if (!b) return;
          const [bx, by] = toWrap(b);
          mdx += bx - px;
          mdy += by - py;
        });
        const tw = tipSize.w, th = tipSize.h;
        const gap = 14;
        const goLeft = rels.length ? mdx > 0 : px > wr.width * 0.62;
        let x = goLeft ? px - gap - tw : px + gap;
        let y = !rels.length || Math.abs(mdy) < 12 * rels.length ? py - th / 2 : mdy > 0 ? py - th + 4 : py - 4;
        x = Math.max(4, Math.min(wr.width - tw - 4, x));
        y = Math.max(4, Math.min(wr.height - th - 4, y));
        tip.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
      }

      // Lands here: the middle of the proposed fact's group arc, in the same 2D
      // overlay space as the tooltip, with its label up and to the right.
      const landsKey = landsArcRef.current;
      const markEl = landsMarkRef.current;
      const markLabel = landsLabelRef.current;
      if (landsKey && markEl && markLabel && stageEl) {
        const lane = ringsRef.current.findIndex((r) => r.arcs.some((arc) => arc.key === landsKey));
        const ring = lane >= 0 ? ringsRef.current[lane] : null;
        const arc = ring?.arcs.find((x) => x.key === landsKey);
        if (ring && arc) {
          const sr = stageEl.getBoundingClientRect();
          const wrr = outer.getBoundingClientRect();
          const k = sr.width / 820;
          const pt = project((arc.start + arc.end) / 2 + lanePhase(lane, ring.speed), ring.radius, 0, cam);
          const mx = sr.left - wrr.left + pt[0] * k;
          const my = sr.top - wrr.top + pt[1] * k;
          markEl.style.transform = `translate(${(mx - 7).toFixed(1)}px, ${(my - 7).toFixed(1)}px)`;
          const lw = markLabel.offsetWidth;
          const lx = Math.max(4, Math.min(wrr.width - lw - 4, mx + 14));
          const ly = Math.max(4, my - 30);
          markLabel.style.transform = `translate(${lx.toFixed(1)}px, ${ly.toFixed(1)}px)`;
        }
      }

      // While the logo uploads, a label tip sits 21px above the sun.
      const upTip = uploadTipRef.current;
      if (upTip && stageEl) {
        const sr = stageEl.getBoundingClientRect();
        const wrr = outer.getBoundingClientRect();
        const k = sr.width / 820;
        const sx = sr.left - wrr.left + 410 * k;
        const sy = sr.top - wrr.top + 258 * k;
        upTip.style.transform = `translate(${(sx - upTip.offsetWidth / 2).toFixed(1)}px, ${(sy - 24 * k - 21 - upTip.offsetHeight).toFixed(1)}px)`;
      }

      // lane tag: a 2D overlay parked at the hovered ring's projected top apex,
      // with a vertical leader tethering it. Updated each frame so it tracks drag
      // + zoom.
      const hr = hoverRingRef.current;
      const plateEl = laneTagPlateRef.current;
      const leaderEl = laneTagLeaderRef.current;
      if (hr != null && plateEl && leaderEl) {
        const target = ringsRef.current[hr]?.radius ?? 0;
        laneRadiusRef.current =
          prevHrRef.current == null || isReduced() || dragging.current
            ? target
            : laneRadiusRef.current + (target - laneRadiusRef.current) * 0.22;
        const R = laneRadiusRef.current;
        const apexY = 258 - sc * R * Math.abs(Math.sin(camRx.current * DEG));
        const ph = plateEl.offsetHeight || 20;
        let topY = apexY - 12 - ph;
        if (topY < 10) topY = 10;
        const botY = topY + ph;
        const lTop = Math.min(botY, apexY);
        const lH = Math.abs(apexY - botY);
        plateEl.style.transform = `translate(410px, ${topY.toFixed(1)}px) translateX(-50%)`;
        leaderEl.style.transform = `translate(410px, ${lTop.toFixed(1)}px) translateX(-50%) scaleY(${(lH / 100).toFixed(3)})`;
      }
      prevHrRef.current = hr;
    };

    const draw = (now: number) => {
      const cx = 410, cy = 258;
      ctx.clearRect(0, 0, 820, 520);
      const t = (now - t0Ref.current) / 1000;
      let pStars = Math.min(1, t / 0.6);
      pStars = 1 - Math.pow(1 - pStars, 3);
      let pBloom = Math.max(0, Math.min(1, (t - 0.12) / 0.48));
      pBloom = 1 - Math.pow(1 - pBloom, 3);
      if (isReduced()) { pStars = 1; pBloom = 1; }

      stars.forEach((s) => {
        const al = pStars * s.al * (isReduced() ? 1 : 0.55 + 0.45 * Math.sin(t * s.tw + s.ph));
        ctx.globalAlpha = al;
        ctx.fillStyle = s.lime ? starAccent : starDim;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, 6.2832);
        ctx.fill();
      });

      const pulse = isReduced() ? 1 : 0.82 + 0.18 * Math.sin(t * 1.7);
      const rg = 56 * pBloom * pulse;
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rg);
      g.addColorStop(0, `rgba(${accentRgb}, ${glowPeak})`);
      g.addColorStop(0.3, `rgba(${accentRgb}, ${glowPeak * 0.25})`);
      g.addColorStop(1, `rgba(${accentRgb}, 0)`);
      ctx.globalAlpha = 1;
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(cx, cy, rg, 0, 6.2832);
      ctx.fill();

      if (!isReduced()) {
        if (t > nextSpawn && streaks.length < 2) {
          streaks.push({ ang: Math.random() * 6.2832, born: t, c: streakColor });
          nextSpawn = t + spawnGap * (1.1 + Math.random() * 0.8);
        }
        const k = 0.62;
        streaks = streaks.filter((s) => {
          const sp = (t - s.born) / 1.5;
          if (sp >= 1) return false;
          const r0 = 330 * (1 - sp);
          const r1 = r0 + 36;
          const x0 = cx + Math.cos(s.ang) * r0, y0 = cy + Math.sin(s.ang) * r0 * k;
          const x1 = cx + Math.cos(s.ang) * r1, y1 = cy + Math.sin(s.ang) * r1 * k;
          const grad = ctx.createLinearGradient(x0, y0, x1, y1);
          grad.addColorStop(0, s.c);
          grad.addColorStop(1, "rgba(0,0,0,0)");
          const fade = sp < 0.12 ? sp / 0.12 : sp > 0.85 ? (1 - sp) / 0.15 : 1;
          ctx.globalAlpha = pStars * fade;
          ctx.strokeStyle = grad;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(x0, y0);
          ctx.lineTo(x1, y1);
          ctx.stroke();
          ctx.fillStyle = s.c;
          ctx.beginPath();
          ctx.arc(x0, y0, 1.9, 0, 6.2832);
          ctx.fill();
          return true;
        });
      }
      ctx.globalAlpha = 1;
    };

    const loop = (now: number) => {
      try {
        scene3d(now);
        draw(now);
      } catch {
        // A transient mid-frame error (e.g. a ref swapped during a re-render)
        // must never kill the loop; keep scheduling so the universe can't freeze.
      }
      raf = requestAnimationFrame(loop);
    };

    measure();
    raf = requestAnimationFrame(loop);
    // Resizes arrive every frame while the rail eases to its card width. Settle
    // on the trailing edge (one measure, one re-render of this memoized stage)
    // and let the stage's own transform transition carry the rescale.
    let settleT = 0;
    let measureRaf = 0;
    const scheduleMeasure = () => {
      layoutFreshUntilRef.current = performance.now() + 150;
      window.clearTimeout(settleT);
      settleT = window.setTimeout(() => {
        cancelAnimationFrame(measureRaf);
        measureRaf = requestAnimationFrame(() => {
          measure();
          layoutFreshUntilRef.current = performance.now() + 520;
        });
      }, 90);
    };
    window.addEventListener("resize", scheduleMeasure);
    // keep the scene fitted when the wrapper resizes (the rail widening for a
    // fact card, the chat rail), not only on window resize.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleMeasure) : null;
    ro?.observe(outer);
    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(measureRaf);
      window.clearTimeout(settleT);
      window.removeEventListener("resize", scheduleMeasure);
      ro?.disconnect();
    };
    // facts/rings intentionally excluded: they flow through refs. Only a changed
    // containment envelope restarts the loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [density, reduced, dark, containAllFacts, fitBox.height, fitBox.width]);

  // ---- drag-to-orbit ----
  const drag = useRef<{
    dx: number;
    dy: number;
    rx: number;
    ry: number;
    factId: string | null;
    pointerId: number;
    axis: "pending" | "horizontal" | "vertical";
  } | null>(null);
  const dragging = useRef(false);
  // fast-sweep hover stabilizers: the ring highlight clears on a short grace
  // timer and planet hover engages on a 60ms dwell.
  const ringClearT = useRef<number | null>(null);
  const factEnterT = useRef<number | null>(null);
  const factClearT = useRef<number | null>(null);
  useEffect(() => () => {
    if (ringClearT.current) clearTimeout(ringClearT.current);
    if (factEnterT.current) clearTimeout(factEnterT.current);
    if (factClearT.current) clearTimeout(factClearT.current);
  }, []);
  const didMove = useRef(false);
  const moveHandlerRef = useRef<((e: PointerEvent) => void) | null>(null);
  const upHandlerRef = useRef<((e: PointerEvent) => void) | null>(null);
  const cancelHandlerRef = useRef<((e: PointerEvent) => void) | null>(null);

  const claimIntroCamera = () => {
    if (!introRef.current || camOwnedRef.current || introDoneRef.current || reduced) return;
    const tt = (performance.now() - t0Ref.current) / 1000;
    let pc = Math.min(1, tt / 1.6);
    pc = 1 - Math.pow(1 - pc, 3);
    const startRx = restRxRef.current - 8;
    const startRy = REST_RY - 6;
    camRx.current = startRx + (camRx.current - startRx) * pc;
    camRy.current = startRy + (camRy.current - startRy) * pc;
    camOwnedRef.current = true;
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!drag.current || e.pointerId !== drag.current.pointerId) return;
    const dx = e.clientX - drag.current.dx;
    const dy = e.clientY - drag.current.dy;
    if (drag.current.axis === "pending") {
      const ax = Math.abs(dx);
      const ay = Math.abs(dy);
      if (Math.max(ax, ay) < 8) return;
      if (ay > ax * 1.1) {
        // Commit to page scrolling for the life of this gesture.
        drag.current.axis = "vertical";
        didMove.current = true;
        return;
      }
      drag.current.axis = "horizontal";
      claimIntroCamera();
      drag.current.rx = camRx.current;
      drag.current.ry = camRy.current;
      dragging.current = true;
      didMove.current = true;
    }
    if (!dragging.current || drag.current.axis === "vertical") return;
    const mt = 3 * zoom; // zoom-aware: a tiny jitter at high zoom isn't a drag
    if (Math.abs(dx) > mt || Math.abs(dy) > mt) didMove.current = true;
    camRy.current = drag.current.ry + dx * 0.35;
    camRx.current = Math.max(-86, Math.min(-8, drag.current.rx - dy * 0.28));
    if (!movedRef.current) {
      movedRef.current = true;
      setMoved(true);
      markInteracted();
    }
  };
  const clearPointerHandlers = () => {
    if (moveHandlerRef.current) window.removeEventListener("pointermove", moveHandlerRef.current);
    if (upHandlerRef.current) window.removeEventListener("pointerup", upHandlerRef.current);
    if (cancelHandlerRef.current) window.removeEventListener("pointercancel", cancelHandlerRef.current);
    moveHandlerRef.current = null;
    upHandlerRef.current = null;
    cancelHandlerRef.current = null;
  };
  const onPointerUp = (e: PointerEvent) => {
    if (!drag.current || e.pointerId !== drag.current.pointerId) return;
    clearPointerHandlers();
    setHoverRing(null); // drop any stale ring highlight the drag left behind
    dragging.current = false;
    if (!didMove.current) selectFactId(drag.current ? drag.current.factId : null);
    drag.current = null;
  };
  const onPointerCancel = (e: PointerEvent) => {
    if (!drag.current || e.pointerId !== drag.current.pointerId) return;
    clearPointerHandlers();
    setHoverRing(null);
    dragging.current = false;
    drag.current = null;
  };
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button != null && e.button !== 0) return;
    if (drag.current) return;
    // A control inside the stage (the logo core) keeps its own click.
    if ((e.target as HTMLElement).closest?.("[data-stage-control]")) return;
    const scrollSafeTouch = allowVerticalPan && e.pointerType === "touch";
    if (!scrollSafeTouch) claimIntroCamera();
    const factEl = (e.target as HTMLElement).closest?.("[data-fact]");
    didMove.current = false;
    drag.current = {
      dx: e.clientX,
      dy: e.clientY,
      rx: camRx.current,
      ry: camRy.current,
      factId: factEl ? factEl.getAttribute("data-fact-id") : null,
      pointerId: e.pointerId,
      axis: scrollSafeTouch ? "pending" : "horizontal",
    };
    dragging.current = !scrollSafeTouch;
    // Capture the pointer to the stage so hover zones stop firing enter/leave
    // while the scene transform moves under a stationary cursor.
    if (!scrollSafeTouch) {
      try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* unsupported */ }
    }
    moveHandlerRef.current = onPointerMove;
    upHandlerRef.current = onPointerUp;
    cancelHandlerRef.current = onPointerCancel;
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerCancel);
    if (!scrollSafeTouch) e.preventDefault();
  };
  useEffect(
    () => () => {
      clearPointerHandlers();
    },
    [],
  );

  // ---- ring hover via math (no DOM hit discs) ----
  // Cast the cursor ray onto the orbit plane (inverting the exact projection the
  // scene renders with) and pick the lane band by radius.
  const onStageMove = (e: React.PointerEvent) => {
    if (dragging.current) return;
    let ri: number | null = null;
    const target = e.target as HTMLElement;
    if (!target.closest?.("[data-fact]") && !target.closest?.("button")) {
      const rect = e.currentTarget.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        const sx = ((e.clientX - rect.left) / rect.width) * 820 - 410;
        const sy = ((e.clientY - rect.top) / rect.height) * 520 - 258;
        const { rx, sc } = camDispRef.current;
        const rxr = rx * DEG;
        const sinrx = Math.sin(rxr), cosrx = Math.cos(rxr);
        const den = sy * cosrx - PERSPECTIVE * sinrx;
        if (Math.abs(den) > 1e-3) {
          const v = (PERSPECTIVE * sy) / (sc * den);
          const persp = PERSPECTIVE - sc * v * cosrx;
          if (persp > 1) {
            const u = (sx * persp) / (PERSPECTIVE * sc);
            const rho = Math.hypot(u, v);
            for (let i = 0; i < rings.length; i++) {
              const half = i < rings.length - 1
                ? (rings[i + 1].radius - rings[i].radius) / 2
                : i > 0
                  ? (rings[i].radius - rings[i - 1].radius) / 2
                  : 16;
              if (rho <= rings[i].radius + half) { ri = i; break; }
            }
          }
        }
      }
    }
    if (ri != null) {
      if (ringClearT.current) { clearTimeout(ringClearT.current); ringClearT.current = null; }
      setHoverRing(ri);
    } else if (!ringClearT.current) {
      ringClearT.current = window.setTimeout(() => {
        ringClearT.current = null;
        setHoverRing(null);
      }, 110);
    }
  };

  const recenter = () => {
    const sx = camRx.current;
    const sy = camRy.current;
    const t0 = performance.now();
    const step = (now: number) => {
      let p = Math.min(1, (now - t0) / 520);
      p = 1 - Math.pow(1 - p, 3);
      camRx.current = sx + (restRxRef.current - sx) * p;
      camRy.current = sy + (REST_RY - sy) * p;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    movedRef.current = false;
    setMoved(false);
    selectFact(null);
  };

  const onStageLeave = () => {
    setHoverRing(null);
    setHoverId(null);
  };

  // ---- keyboard model ----
  // Facts per lane in angle order. Rings turn rigidly, so ang0 order IS the
  // order on screen at any moment. The last lane is the off-lane.
  const laneFacts = useMemo(() => {
    const lanes: number[][] = Array.from({ length: rings.length + 1 }, () => []);
    facts.forEach((f, i) => lanes[f.ring >= 0 ? f.ring : rings.length].push(i));
    const norm = (a: number) => ((a % TAU) + TAU) % TAU;
    lanes.forEach((list) => list.sort((a, b) => norm(facts[a].ang0) - norm(facts[b].ang0)));
    return lanes;
  }, [facts, rings.length]);

  const announce = (i: number) => {
    const f = facts[i];
    if (!f) return;
    setLiveMsg(`${stripFactPrefix(f.label, f.concept)}, ${f.path || "Not in an orbit"}`);
  };

  const moveActive = (key: string) => {
    const laneOf = (i: number) => (facts[i].ring >= 0 ? facts[i].ring : rings.length);
    const start = kbdIdx ?? selected;
    if (start == null) {
      const first = laneFacts.find((l) => l.length)?.[0];
      if (first == null) return;
      setKbdId(facts[first].id);
      announce(first);
      return;
    }
    const lane = laneOf(start);
    const list = laneFacts[lane];
    let next = start;
    if (key === "ArrowRight" || key === "ArrowLeft") {
      const at = list.indexOf(start);
      const step = key === "ArrowRight" ? 1 : -1;
      next = list[(at + step + list.length) % list.length];
    } else {
      const dir = key === "ArrowUp" ? 1 : -1;
      const f0 = facts[start];
      const a0 = f0.ang0 + lanePhase(lane, f0.speed);
      for (let l = lane + dir; l >= 0 && l < laneFacts.length; l += dir) {
        if (!laneFacts[l].length) continue;
        let best = laneFacts[l][0];
        let bestD = Infinity;
        for (const i of laneFacts[l]) {
          const f = facts[i];
          const d = Math.abs(wrapPi(f.ang0 + lanePhase(l, f.speed) - a0));
          if (d < bestD) { bestD = d; best = i; }
        }
        next = best;
        break;
      }
    }
    setKbdId(facts[next].id);
    announce(next);
  };

  const onStageKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    kbdFocusRef.current = true;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowRight":
      case "ArrowUp":
      case "ArrowDown":
        e.preventDefault();
        moveActive(e.key);
        break;
      case "Enter":
      case " ":
        if (kbdIdx != null) {
          e.preventDefault();
          selectFact(kbdIdx);
        }
        break;
      case "Escape":
        if (kbdIdx != null) {
          e.preventDefault();
          setKbdId(null);
          setLiveMsg("");
        } else if (selected != null) {
          e.preventDefault();
          selectFact(null);
        }
        break;
      default:
        break;
    }
  };

  const sel = selFact;
  const hoverRingFactCount = (() => {
    const ring = hoverRing != null ? rings[hoverRing] : undefined;
    if (!ring) return 0;
    if (!ring.arcs.length) return facts.filter((f) => f.ring === hoverRing).length;
    return new Set(ring.arcs.flatMap((arc) => arc.factIds)).size;
  })();
  const factLabelById = new Map(facts.map((f) => [f.id, f.label] as const));
  const tipFact = focusFact;
  const tipRelCount = tipFact ? (related?.[tipFact.id] ?? []).filter((id) => indexById.has(id)).slice(0, 5).length : 0;

  const stageStyle: CSSProperties = {
    position: "absolute", left: "50%", top: "50%", width: 820, height: 520,
    cursor: "grab",
    transformOrigin: "center", touchAction: allowVerticalPan ? "pan-y" : "none", userSelect: "none",
    perspective: `${PERSPECTIVE}px`, perspectiveOrigin: "50% 50%",
    transform: `translate(-50%,-50%) scale(${(scale * zoom).toFixed(3)}) translateY(${fitBox.offsetY.toFixed(1)}px)`,
    transition: "transform .42s cubic-bezier(0.16,1,0.3,1)",
  };
  // Everything in the scene scales with the stage fit, so a 1px hairline on a
  // mark would drop below a device pixel on a small stage. Marks multiply their
  // borders by this to stay about one screen pixel wide.
  (stageStyle as unknown as Record<string, string>)["--co-orbit-px"] = (1.15 / Math.max(0.2, scale * zoom)).toFixed(3);
  const factListVisible = showFactList ?? showControls;
  const stageScale = scale * zoom;
  // The stage's transform transitions for .42s after a scale change: keep
  // re-reading the tooltip's frame of reference until it has settled.
  useEffect(() => {
    layoutFreshUntilRef.current = performance.now() + 520;
  }, [stageScale, fitBox.offsetY]);

  return (
    <>
      {/* Accessible fact list: one button per fact, naming its similarity
          relations so the lines are exposed non-visually (SC 1.1.1). */}
      {factListVisible ? (
        <ul className="co-sr-only" aria-label="Brand facts in the universe">
          {facts.map((f, i) => {
            const relLabels = (related?.[f.id] ?? [])
              .map((id) => factLabelById.get(id))
              .filter((l): l is string => !!l)
              .map((l) => stripFactPrefix(l));
            return (
              <li key={`sr-${f.id}`}>
                <button type="button" onClick={() => selectFact(i)}>
                  {stripFactPrefix(f.label, f.concept)}, {f.kind === "new" ? "new this week" : "established"}, in {f.path || "no orbit"}
                  {relLabels.length ? `. Close to ${relLabels.join(", ")}` : ""}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      <div className="co-sr-only" aria-live="polite">{liveMsg}</div>

      {/* The one line of instruction, above the orbit in place of a caption, and
          outside the focusable stage so its focus ring never runs through it.
          Nothing textual sits on the orbit itself at rest. */}
      <p className="co-universe-hint" aria-hidden="true" style={{ animation: `coFadeIn .6s ${E} ${intro ? "1.6s" : "0.2s"} both` }}>
        {HINT_COPY}
      </p>

      <div
        ref={outerRef}
        className="co-stage-wrap co-universe-orbit"
        tabIndex={0}
        role="application"
        aria-label="Universe orbit. Arrow keys move between facts, Enter opens one."
        onKeyDown={onStageKeyDown}
        onFocus={(e) => {
          if (e.target === e.currentTarget && e.currentTarget.matches(":focus-visible")) kbdFocusRef.current = true;
        }}
        onBlur={(e) => {
          if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
          kbdFocusRef.current = false;
          setKbdId(null);
        }}
        onPointerEnter={() => { pointerInsideRef.current = true; }}
        onPointerLeave={() => { pointerInsideRef.current = false; }}
        onPointerDownCapture={() => { kbdFocusRef.current = false; }}
        style={{ position: "relative", width: "100%", flex: "1 1 auto", minHeight: 340, marginTop: 8, overflow: "hidden" }}
      >
        <div ref={stageRef} onPointerDown={onPointerDown} onPointerMove={onStageMove} onPointerLeave={onStageLeave} style={stageStyle}>
          <canvas ref={canvasRef} width={820} height={520} aria-hidden="true" style={{ position: "absolute", inset: 0, width: 820, height: 520, pointerEvents: "none" }} />
          {/* hover lines: 2D, canvas coordinate space; the loop positions one
              straight hairline per neighbour of the lit planet */}
          <svg width={820} height={520} aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 3 }}>
            <g ref={relGroupRef} style={{ opacity: relIdxs.length ? 0.7 : 0, transition: `opacity .2s ${E}` }}>
              {relIdxs.map((j) => (
                <line key={`rel-${facts[j]?.id ?? j}`} data-rel={j} stroke="var(--co-muted)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
              ))}
            </g>
          </svg>
          {/* Sun core in 2D at the scene's projected center, behind the orbit,
              layered on the canvas radial bloom. */}
          <div style={{ position: "absolute", left: 410, top: 258, transform: "translate(-50%,-50%)", pointerEvents: "none", zIndex: 2 }}>
            <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: 100, height: 100, borderRadius: "50%", background: "radial-gradient(circle, rgba(var(--co-accent-rgb),.28) 0%, rgba(var(--co-accent-rgb),.08) 46%, rgba(var(--co-accent-rgb),0) 72%)", animation: intro && !reduced ? `coSunRise .48s ${E} .12s both` : undefined }} />
            {onUploadLogo ? (
              <button type="button" data-stage-control onClick={() => fileRef.current?.click()} disabled={uploadingLogo} aria-busy={uploadingLogo} aria-label={uploadingLogo ? "Uploading your brand logo" : sunCoreLabel} title={sunCoreLabel} style={{ ...sunCoreStyle, cursor: uploadingLogo ? "progress" : "pointer", pointerEvents: "auto" }}>
                {sunCoreMark}
              </button>
            ) : (
              <div aria-hidden="true" style={sunCoreStyle}>{sunCoreMark}</div>
            )}
          </div>
          {onUploadLogo ? (
            <input ref={fileRef} type="file" accept={BRAND_LOGO_ACCEPT} onChange={onLogoPick} style={{ display: "none" }} />
          ) : null}
          {/* lane tag: 2D overlay, parked by the loop at the hovered ring's top
              apex. Hover-only: the subject name and its fact count. */}
          <div ref={laneTagLeaderRef} aria-hidden="true" style={{ position: "absolute", left: 0, top: 0, width: 1, height: 100, transformOrigin: "top", background: "var(--co-line2)", opacity: hoverRing != null ? 1 : 0, transition: `opacity .18s ${E}`, pointerEvents: "none", zIndex: 5, willChange: "transform" }} />
          <div ref={laneTagPlateRef} aria-hidden="true" style={{ position: "absolute", left: 0, top: 0, display: "flex", alignItems: "center", gap: 7, background: "var(--co-ground)", border: "1px solid var(--co-line2)", padding: "3px 9px", whiteSpace: "nowrap", opacity: hoverRing != null ? 1 : 0, transition: `opacity .18s ${E}`, pointerEvents: "none", zIndex: 5, willChange: "transform" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--co-text)" }}>{hoverRing != null ? (rings[hoverRing]?.label ?? "") : ""}</span>
            {hoverRing != null ? (
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: ".06em", color: "var(--co-muted)" }}>
                · {hoverRingFactCount} fact{hoverRingFactCount === 1 ? "" : "s"}
              </span>
            ) : null}
          </div>
          {/* Rings: their own 3D root, painted under the planets' root. */}
          <div ref={ringRootRef} aria-hidden="true" style={{ position: "absolute", left: 410, top: 258, transformStyle: "preserve-3d", willChange: "transform", pointerEvents: "none" }}>
            {rings.map((ring, ri) => {
              const r = ring.radius;
              const lit = litRing === ri;
              const ringIn = intro && !introDone && !reduced;
              // Outer rings recede a little, never below legibility.
              const resting = +Math.max(0.85, 1 - ri * 0.12).toFixed(3);
              const ringStyle: CSSProperties = {
                position: "absolute", left: 0, top: 0, width: r * 2, height: r * 2, margin: `${-r}px 0 0 ${-r}px`,
                transform: "rotateX(90deg)", opacity: lit ? 1 : resting,
                pointerEvents: "none", transition: `opacity .18s ${E}`,
              };
              if (ringIn) ringStyle.animation = `coRingIn .42s ${E} ${(0.62 + ri * 0.06).toFixed(2)}s both`;
              (ringStyle as unknown as Record<string, string | number>)["--co-ring-o"] = resting;
              const arcStroke = (arc: RingArc) => {
                const on = lit || litArcKeys.has(arc.key);
                if (!arc.charted) return { stroke: on ? "var(--co-muted)" : "var(--co-faint)", width: on ? 1.4 : 1, dash: "6 6" };
                return { stroke: on ? "var(--co-muted)" : "var(--universe-arc)", width: litArcKeys.has(arc.key) ? 1.6 : on ? 1.4 : 1, dash: undefined };
              };
              return (
                <div key={`ring-${ring.id}`} style={ringStyle}>
                  <svg
                    ref={(el) => { ringSvgElsRef.current[ri] = el; }}
                    width={r * 2}
                    height={r * 2}
                    viewBox={`${-r} ${-r} ${r * 2} ${r * 2}`}
                    overflow="visible"
                    style={{ display: "block", willChange: "transform" }}
                  >
                    {ring.arcs.length ? (
                      ring.arcs.map((arc) => {
                        const s = arcStroke(arc);
                        return (
                          <path
                            key={arc.key}
                            d={arcPath(r, arc.start, arc.end)}
                            fill="none"
                            stroke={s.stroke}
                            strokeWidth={s.width}
                            strokeDasharray={s.dash}
                            vectorEffect="non-scaling-stroke"
                            style={{ transition: `stroke .18s ${E}, stroke-width .18s ${E}` }}
                          />
                        );
                      })
                    ) : (
                      <circle r={r} fill="none" stroke={lit ? "var(--co-muted)" : "var(--universe-arc)"} strokeWidth={lit ? 1.4 : 1} vectorEffect="non-scaling-stroke" />
                    )}
                  </svg>
                </div>
              );
            })}
          </div>

          {/* Echoes and planets: billboarded markers in a 3D root with no ring
              plane in it, so nothing slices them. */}
          <div ref={sceneRef} aria-hidden="true" style={{ position: "absolute", left: 410, top: 258, transformStyle: "preserve-3d", willChange: "transform" }}>
            {/* Echoes: a second filing (same subject or cross-filed), drawn as a
                hollow square where that group sits. Never interactive, never
                counted. */}
            {echoes.map((echo, k) => (
              <div key={`echo-${echo.arcKey}-${echo.factId}`} data-echo={k} aria-hidden="true" style={{ position: "absolute", left: 0, top: 0, width: 0, height: 0, pointerEvents: "none", transformOrigin: "0 0", transformStyle: "preserve-3d" }}>
                <span style={{ position: "absolute", left: 0, top: 0, width: 7, height: 7, transform: "translate(-50%,-50%)", border: "calc(1px * var(--co-orbit-px, 1)) solid var(--co-constant)", boxSizing: "border-box", backfaceVisibility: "hidden" }} />
              </div>
            ))}

            {facts.map((f, i) => {
              const isFocus = focusIdx === i;
              const isKbd = kbdIdx === i;
              const isSelected = selected === i;
              const isRel = relSet.has(i);
              const isMate = selected != null && !isSelected && mateSet.has(i);
              const ringEcho = litRing != null && f.ring === litRing;
              const isNew = f.kind === "new";
              // One mark per state. The opened fact is a flat accent square; the
              // lit planet grows and turns ink; its neighbours and its
              // group-mates turn ink too. New facts keep their accent round.
              // Squares are rounded (Universe / Fact mark, 8 Oct: r1.5 on 5).
              let sz = isNew ? 11 : 7;
              let bg = isNew ? "var(--co-accent)" : "var(--co-constant)";
              let round = isNew;
              if (isSelected) { sz = 10; bg = "var(--co-accent)"; round = false; }
              else if (isFocus && !isNew) { sz = 8; bg = "var(--co-text)"; }
              else if ((isRel || isMate) && !isNew) { sz = 6; bg = "var(--co-text)"; }
              const dotMotion = `width .18s ${E}, height .18s ${E}, background .18s ${E}, border-radius .18s ${E}`;
              const dotStyle: CSSProperties = { width: sz, height: sz, borderRadius: round ? "50%" : "30%", background: bg, flex: "none", transition: dotMotion };
              const fresh = freshSet.has(f.id) && motion;
              return (
                <div key={`fact-${f.id}`} data-fact={i} data-fact-id={f.id} onPointerEnter={() => { if (dragging.current) return; if (factClearT.current) { clearTimeout(factClearT.current); factClearT.current = null; } if (factEnterT.current) clearTimeout(factEnterT.current); factEnterT.current = window.setTimeout(() => { factEnterT.current = null; setHoverId(f.id); }, 60); }} onPointerLeave={() => { if (factEnterT.current) { clearTimeout(factEnterT.current); factEnterT.current = null; } if (dragging.current) return; if (factClearT.current) clearTimeout(factClearT.current); factClearT.current = window.setTimeout(() => { factClearT.current = null; setHoverId((cur) => (cur === f.id ? null : cur)); }, 90); }} style={{ position: "absolute", left: 0, top: 0, width: 0, height: 0, cursor: "pointer", touchAction: allowVerticalPan ? "pan-y" : "none", transformOrigin: "0 0", transformStyle: "preserve-3d" }}>
                  {/* Mark is a fixed 32px layer so the 3D quad stays tiny. */}
                  <div style={{ position: "absolute", left: 0, top: 0, width: 32, height: 32, transform: "translate(-50%,-50%)", overflow: "hidden", contain: "strict", backfaceVisibility: "hidden", pointerEvents: "auto", filter: ringEcho && !isFocus && !isSelected ? "brightness(1.18)" : "none", transition: `filter .2s ${E}` }}>
                    {/* invisible hit pad: the visible dot is far too small a target */}
                    <span aria-hidden style={{ position: "absolute", left: "50%", top: "50%", width: 28, height: 28, transform: "translate(-50%,-50%)", background: "transparent" }} />
                    {isKbd ? (
                      <span aria-hidden style={{ position: "absolute", left: "50%", top: "50%", width: 16, height: 16, transform: "translate(-50%,-50%)", border: "calc(1.5px * var(--co-orbit-px, 1)) solid var(--co-accent)", boxSizing: "border-box" }} />
                    ) : isFocus ? (
                      <span aria-hidden style={{ position: "absolute", left: "50%", top: "50%", width: 18, height: 18, transform: "translate(-50%,-50%)", border: "calc(1px * var(--co-orbit-px, 1)) solid var(--co-text2)", boxSizing: "border-box" }} />
                    ) : null}
                    <span className={fresh ? "co-glint" : undefined} style={{ ...dotStyle, position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", animationDelay: fresh ? `${glintDelayFor(f.id).toFixed(2)}s` : undefined }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* The hover card: 2D, outside the 3D scene, placed by the loop beside the
            lit planet. It replaces the old in-scene name callout. */}
        <div
          ref={tooltipRef}
          className="co-universe-tip"
          aria-hidden="true"
          data-open={tipFact ? "true" : undefined}
        >
          {tipFact ? (
            <>
              <span className="co-universe-tip-name" dir="auto">{stripFactPrefix(tipFact.label, tipFact.concept)}</span>
              <span className="co-universe-tip-path">{tipFact.path || "Not in an orbit"}</span>
              {tipRelCount || tipFact.id !== sel?.id ? (
                <span className="co-universe-tip-note">
                  {[
                    tipRelCount ? `Close to ${tipRelCount} fact${tipRelCount === 1 ? "" : "s"}.` : "",
                    tipFact.id !== sel?.id ? "Click to open." : "",
                  ].filter(Boolean).join(" ")}
                </span>
              ) : null}
            </>
          ) : null}
        </div>

        {uploadingLogo ? (
          <div ref={uploadTipRef} className="co-universe-upload-tip" aria-hidden="true">
            Uploading your brand logo
          </div>
        ) : null}

        {/* Review mode: where the proposed fact would land. */}
        <div ref={landsMarkRef} className="co-universe-lands-mark" aria-hidden="true" data-open={landsHere ? "true" : undefined} />
        <div ref={landsLabelRef} className="co-universe-lands-label" aria-hidden="true" data-open={landsHere ? "true" : undefined}>
          {landsHere ? `Lands here · ${landsHere.label}` : null}
        </div>

        {showControls && showHint ? (
          <span style={{ position: "absolute", left: "50%", bottom: 14, transform: "translateX(-50%)", pointerEvents: "none", zIndex: 10, fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--co-faint)", opacity: moved || hintAutoHidden ? 0 : 1, transition: `opacity .6s ${E}`, animation: `coFadeIn .6s ${E} ${intro ? "2.3s" : ".3s"} backwards` }}>Drag to orbit · Pinch to zoom</span>
        ) : null}

        {showControls ? (
          <div className="co-stage-tools" style={{ position: "absolute", right: 16, bottom: 14, display: "flex", flexDirection: "column", gap: 6, zIndex: 10, animation: `coFadeIn .6s ${E} ${intro ? "2.3s" : ".3s"} backwards` }}>
          {moved ? (
            <button type="button" className="co-icon-ghost" onClick={recenter} aria-label="Recenter the universe" title="Recenter" style={{ width: 30, height: 30, display: "grid", placeItems: "center", cursor: "pointer" }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></svg>
            </button>
          ) : null}
          <button type="button" className="co-icon-ghost" onClick={() => { if (!movedRef.current) { movedRef.current = true; setMoved(true); } markInteracted(); setZoom((z) => Math.min(2.2, +(z + 0.18).toFixed(2))); }} aria-label="Zoom in" title="Zoom in" style={{ width: 30, height: 30, display: "grid", placeItems: "center", cursor: "pointer", fontSize: 17, lineHeight: 1, fontFamily: "var(--font-body)" }}>+</button>
          <button type="button" className="co-icon-ghost" onClick={() => { if (!movedRef.current) { movedRef.current = true; setMoved(true); } markInteracted(); setZoom((z) => Math.max(0.55, +(z - 0.18).toFixed(2))); }} aria-label="Zoom out" title="Zoom out" style={{ width: 30, height: 30, display: "grid", placeItems: "center", cursor: "pointer", fontSize: 20, lineHeight: 1, fontFamily: "var(--font-body)" }}>−</button>
          </div>
        ) : null}
      </div>
    </>
  );
}

// Memoized: the view re-renders on every hover change elsewhere on the page, but
// this canvas stage's props are referentially stable, so a shallow compare skips
// the expensive re-render.
export default memo(UniverseStage);
